import { css } from '@emotion/react';
import React from 'react';

import {
  GainSelfAbility,
  getGainSelfCardAbility,
} from '@mythos/game/CardAbilities';
import { CardAffinity } from '@mythos/game/CardTypes';
import { CardDef } from '@mythos/game/GameModel';
import { probabilityWinningMultiBattle } from '@mythos/game/Probability';
import { ageString } from '@mythos/game/Utility';
import { range } from 'lodash';
import BaseCardView from './BaseCardView';
import { SymbolWithNumber } from './CardHelpers';
import { GainSelfEffect } from './CardView';
import { CardAffinityToFrameColor } from './ColorPalette';
import Symbols from './Symbols';

const BASIC_FRAME_COLOR = CardAffinityToFrameColor[CardAffinity.Basic];

export type ScavengeReferenceCardProps = {
  scavengeCards: CardDef[];
};

export const ScavengeReferenceCardView = React.forwardRef<
  HTMLDivElement,
  ScavengeReferenceCardProps
>((props, ref) => {
  const { scavengeCards } = props;

  const sortedCards = [...scavengeCards].sort((a, b) => a.age - b.age);

  return (
    <BaseCardView
      ref={ref}
      cardStyle={ReferenceCardStyles.card}
      landscape={true}
    >
      <div css={ReferenceCardStyles.content}>
        <div css={ReferenceCardStyles.titleContainer}>
          <div css={ReferenceCardStyles.title}>Scavenge</div>
          <div css={ReferenceCardStyles.subtitle}>Different per age</div>
        </div>
        <div css={ReferenceCardStyles.columnsContainer}>
          {sortedCards.map((card) => (
            <div key={card.defID} css={ReferenceCardStyles.column}>
              <div css={ReferenceCardStyles.ageLabel}>
                {ageString(card.age)}
              </div>
              <div css={ReferenceCardStyles.costGainContainer}>
                <div css={ReferenceCardStyles.costContainer}>
                  <SymbolWithNumber
                    symbol={Symbols.GOLD}
                    number={card.cost}
                    height={16}
                    fontSize={9}
                  />
                </div>
                <GainSelfEffect
                  ability={getGainSelfCardAbility(card)}
                  borderColor={BASIC_FRAME_COLOR}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </BaseCardView>
  );
});

export type MercenaryReferenceCardProps = {
  mercenaryCards: CardDef[];
};

export const MercenaryReferenceCardView = React.forwardRef<
  HTMLDivElement,
  MercenaryReferenceCardProps
>((props, ref) => {
  const { mercenaryCards } = props;

  const age1Cards = mercenaryCards
    .filter((c) => c.age === 1)
    .sort((a, b) => a.cost - b.cost);

  const variants = age1Cards.map((card, index) => ({
    label: String.fromCharCode(65 + index),
    cost: card.cost,
    card,
  }));

  return (
    <BaseCardView
      ref={ref}
      cardStyle={ReferenceCardStyles.card}
      landscape={true}
    >
      <div css={ReferenceCardStyles.content}>
        <div css={ReferenceCardStyles.titleContainer}>
          <div css={ReferenceCardStyles.title}>Mercenary</div>
          <div css={ReferenceCardStyles.subtitle}>Choose a variant</div>
        </div>
        <div css={ReferenceCardStyles.columnsContainer}>
          {variants.map((variant) => (
            <div key={variant.label} css={ReferenceCardStyles.column}>
              <div css={ReferenceCardStyles.variantLabel}>{variant.label}</div>
              <div css={ReferenceCardStyles.costGainContainer}>
                <div css={ReferenceCardStyles.costContainer}>
                  <SymbolWithNumber
                    symbol={Symbols.GOLD}
                    number={variant.cost}
                    height={16}
                    fontSize={9}
                  />
                </div>
                <GainSelfEffect
                  ability={
                    getGainSelfCardAbility(variant.card) ??
                    new GainSelfAbility('0M')
                  }
                  borderColor={BASIC_FRAME_COLOR}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </BaseCardView>
  );
});

// Format a clash win probability for the reference card. Below 99% the extra
// precision doesn't matter, so we show a whole percent. In the 99-band we keep
// adding decimals while each digit is >= 5 and stop at the first digit < 5 (a
// digit < 5 rounds down, so dropping it loses nothing). This reads the run of
// nines cleanly with no false precision: 99% -> 99.5% -> 99.9% -> 99.998%.
//
// First round to 0.001% (as the old integer-only formatter did): the raw
// computation carries float noise — a guaranteed win lands at 99.99999…% and a
// true coin flip at 49.99999…% — which would otherwise produce absurd tails.
// We then work in integer thousandths so digit truncation isn't bitten by
// float (e.g. 99.998 stored as 99.99799… truncating to 99.997).
function formatWinPct(p: number): string {
  const pct = Math.round(p * 1e5) / 1e3;
  if (pct >= 100) return '100';
  if (pct < 99) return Math.floor(pct).toString();

  const milli = Math.round(pct * 1000); // exact integer thousandths of a percent
  const whole = Math.floor(milli / 1000);
  const digits = [
    Math.floor((milli % 1000) / 100), // tenths
    Math.floor((milli % 100) / 10), // hundredths
    milli % 10, // thousandths
  ];
  let decimals = 0;
  while (decimals < digits.length && digits[decimals] >= 5) decimals++;
  if (decimals === 0) return String(whole);
  return `${whole}.${digits.slice(0, decimals).join('')}`;
}

// Lose % is just the complement of the win display, shown at matching precision
// so the two always sum to 100 (50 -> 50, 99 -> 1, 99.998 -> 0.002, 100 -> 0).
function formatLosePct(p: number): string {
  const win = formatWinPct(p);
  const decimals = win.includes('.') ? win.split('.')[1].length : 0;
  return (100 - Number(win)).toFixed(decimals);
}

export type ProbabilityReferenceMode = 'win' | 'lose';

export const ProbabilityReferenceCardView = React.forwardRef<
  HTMLDivElement,
  { mode: ProbabilityReferenceMode }
>(({ mode }, ref) => {
  const format = mode === 'lose' ? formatLosePct : formatWinPct;
  const rows = range(0, 16).map((bonus) => {
    const cells = [1, 2, 3].map((age) => {
      if (bonus > 5 * age) return null;
      const p = probabilityWinningMultiBattle(age, bonus, [[age, 0]]);
      return format(p);
    });
    return { bonus, cells };
  });

  return (
    <BaseCardView ref={ref} cardStyle={ProbRefStyles.card}>
      <div css={ProbRefStyles.content}>
        <div css={ProbRefStyles.titleContainer}>
          <div css={ProbRefStyles.title}>Clash Probabilities</div>
          <div css={ProbRefStyles.subtitle}>
            {mode === 'lose' ? 'Lose % by advantage' : 'Win % by advantage'}
          </div>
        </div>
        <table css={ProbRefStyles.table}>
          <thead>
            <tr>
              <th>+</th>
              <th>I</th>
              <th>II</th>
              <th>III</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ bonus, cells }) => (
              <tr key={bonus}>
                <td>{bonus}</td>
                {cells.map((value, i) => (
                  <td key={i}>{value !== null ? `${value}%` : ''}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </BaseCardView>
  );
});

const ProbRefStyles = {
  card: {
    backgroundColor: 'rgb(60, 45, 38)',
    backgroundImage:
      'linear-gradient(135deg, rgb(70, 52, 44) 0%, rgb(50, 38, 32) 100%)',
  } as React.CSSProperties,
  content: css({
    display: 'flex',
    flexDirection: 'column',
    padding: '0 2px 2px 2px',
    height: '100%',
  }),
  titleContainer: css({
    backgroundColor: 'black',
    paddingTop: 2,
    paddingBottom: 1,
    marginLeft: -2,
    marginRight: -2,
    marginBottom: 1,
  }),
  title: css({
    fontSize: 7,
    fontWeight: 'bold',
    color: 'rgb(215, 200, 180)',
    textAlign: 'center',
    fontFamily: 'Mythos-CardTitle',
  }),
  subtitle: css({
    fontSize: 4.5,
    color: 'rgb(170, 150, 130)',
    textAlign: 'center',
  }),
  table: css({
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 4.5,
    color: 'rgb(200, 180, 160)',

    '& th': {
      fontWeight: 'bold',
      fontSize: 5,
      padding: '1px',
      borderBottom: '0.5px solid rgba(200, 180, 160, 0.3)',
      color: 'rgb(215, 200, 180)',
    },
    '& td': {
      padding: '0.5px 1px',
      textAlign: 'center',
    },
    '& td:first-of-type': {
      fontWeight: 'bold',
      color: 'rgb(215, 200, 180)',
    },
    '& tbody tr:nth-child(odd)': {
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
    },
  }),
};

const ReferenceCardStyles = {
  card: {
    backgroundColor: 'rgb(60, 45, 38)',
    backgroundImage:
      'linear-gradient(135deg, rgb(70, 52, 44) 0%, rgb(50, 38, 32) 100%)',
  } as React.CSSProperties,
  content: css({
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: 4,
    paddingRight: 4,
    paddingBottom: 2,
    height: '100%',
  }),
  titleContainer: css({
    backgroundColor: 'black',
    paddingTop: 2,
    paddingBottom: 2,
    marginLeft: -4,
    marginRight: -4,
    marginBottom: 2,
  }),
  title: css({
    fontSize: 9,
    fontWeight: 'bold',
    color: 'rgb(215, 200, 180)',
    textAlign: 'center',
    fontFamily: 'Mythos-CardTitle',
  }),
  subtitle: css({
    fontSize: 6,
    color: 'rgb(170, 150, 130)',
    textAlign: 'center',
  }),
  columnsContainer: css({
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 3,
  }),
  column: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  }),
  costGainContainer: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: 'black',
    borderRadius: 8,
    paddingLeft: 2,
    paddingRight: 2,
    paddingTop: 3,
    paddingBottom: 4,
    marginTop: 1,
    marginBottom: 1,
  }),
  costContainer: css({}),
  ageLabel: css({
    fontSize: 7,
    color: 'rgb(200, 180, 160)',
    textAlign: 'center',
    fontWeight: 'bold',
    marginBottom: 3,
  }),
  variantLabel: css({
    fontSize: 7,
    color: 'rgb(200, 180, 160)',
    textAlign: 'center',
    fontWeight: 'bold',
    marginBottom: 3,
  }),
} as const;
